import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchItems,
  fetchInventory,
  fetchUsers,
  fetchBranches,
  fetchSalesHistory,
  fetchLayaway,
  fetchSaleItems,
  createSale,
  updateSale,
  deleteSale,
  createItem,
  createInventory,
  fetchSettings,
  createReturn,
  fetchSalePayments,
} from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
  ShoppingCart,
  Search,
  Plus,
  Minus,
  ArrowRight,
  CircleDollarSign,
  CreditCard,
  History,
  Download,
  Eye,
  Trash2,
  Pencil,
  RotateCcw,
  Calendar as CalendarIcon,
  Printer,
  FileText,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EditPricePopover } from '@/components/sales/EditPricePopover';
import { AddCustomItemDialog } from '@/components/sales/AddCustomItemDialog';
import ReturnDialog from '@/components/sales/ReturnDialog';
import { SplitPaymentDialog } from '@/components/sales/SplitPaymentDialog';
import { toast } from 'sonner';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/contexts/AuthContext';
import { debounce } from 'lodash';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { format, startOfToday, endOfToday, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays } from 'date-fns';
import { DateTimePicker } from '@/components/ui/datetime-picker';

interface ItemRaw {
  id: string;
  name: string;
  sku: string;
  buying_price: string;
  selling_price: string;
  quantity: number;
  vat_enabled: number | undefined;
  barcode?: string | null;
  imeis?: string | null;
}
interface InventoryRaw {
  item_id: string;
  branch_id: string;
  quantity: string;
}
interface Product {
  id: string;
  name: string;
  sku: string;
  buying_price: number;
  selling_price: number;
  quantity: number;
  isCustom?: boolean;
  vat_enabled: number;
  barcode?: string | null;
  imeis?: string | null;
}
interface CartItem {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  buying_price: number;
  quantity: number;
  isCustom?: boolean;
  vat_enabled: number;
  barcode?: string | null;
  imeis?: string | null;
}
interface User {
  id: string;
  name: string;
}
interface Branch {
  id: string;
  name: string;
}
interface Sale {
  id: string;
  invoice_number: number;
  user_id: string;
  branch_id: string;
  branch_name?: string;
  total_amount: number;
  subtotal?: number;
  discount_type?: 'percentage' | 'fixed' | null;
  discount_value?: number;
  payment_method?: string;
  is_split_payment?: boolean;
  has_returns?: boolean;
  total_returned_amount?: number;
  net_amount?: number;
  created_at: string;
  record_type?: 'sale' | 'layaway_payment';
  layaway_id?: string | null;
  layaway_number?: number | string | null;
  item_count?: number;
  status?: string;
  product_handling?: string;
  items?: SaleItem[];
  customer?: CustomerDetails;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
}
interface SaleItem {
  id: string;
  sale_id: string;
  item_id: string;
  quantity: number;
  price: number;
}

interface LayawayHistoryDetails {
  layaway_id: string;
  layaway_number: string | number | null;
  items: Array<{
    id: string;
    name?: string;
    sku?: string;
    item_id?: string;
    quantity: number;
    price: number;
    barcode?: string | null;
    vat_enabled?: number;
    vat?: number;
  }>;
  payments: Array<{
    id: string;
    payment_date: string;
    amount: number;
    payment_method?: string | null;
    notes?: string | null;
  }>;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
  status?: string;
  product_handling?: string;
}
interface CustomerDetails {
  name?: string | null;
  phone?: string | null;
  address?: string | null;
}
interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

const ProductCard = React.memo(({ p, addToCart, currencySymbol }: { p: Product; addToCart: (p: Product) => void; currencySymbol: string }) => (
  <Card
    className={`relative p-2 flex flex-col rounded-md shadow-sm transition-transform transform hover:scale-[1.02] hover:shadow-md ${
      p.quantity <= 0 && !p.isCustom ? 'opacity-50 cursor-not-allowed' : ''
    }`}
    style={{
      background: p.isCustom
        ? 'linear-gradient(135deg, #e0e7ff 0%, #c3dafe 100%)'
        : 'linear-gradient(135deg, #e6f3ff 0%, #d1e9ff 100%)',
      border: 'none',
    }}
  >
    <span className="absolute top-2 right-2 inline-flex min-w-7 items-center justify-center rounded-full bg-blue-600 text-white text-[11px] font-semibold px-2 py-0.5 shadow">
      {p.quantity}
    </span>
    <div className="pr-9">
      <span className="font-semibold text-[13px] leading-tight text-gray-800">{p.name}</span>
    </div>
    <span className="text-[11px] text-gray-600 mt-0.5">SKU: {p.sku}</span>
    <span className="text-[12px] font-medium text-gray-700 mt-1">
      {currencySymbol}{p.selling_price.toFixed(2)}
    </span>
    <Button
      variant="outline"
      size="icon"
      className="mt-2 h-7 w-7 bg-white text-gray-800 hover:bg-gray-100 rounded-full shadow"
      onClick={() => addToCart(p)}
      disabled={p.quantity <= 0 && !p.isCustom}
    >
      <Plus className="h-3.5 w-3.5" />
    </Button>
  </Card>
));

export default function SalesPage() {
  useEffect(() => {
    document.title = 'Sales - Vixalor';
  }, []);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const userBranchId = user?.branch_id;
  const userId = user?.id;

  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [visibleProductsCount, setVisibleProductsCount] = useState(12);
  const productsPerLoad = 12;
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [includeCustomerDetails, setIncludeCustomerDetails] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({});
  const [sales, setSales] = useState<Sale[]>([]);
  const [viewSaleDialogOpen, setViewSaleDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [editSaleDialogOpen, setEditSaleDialogOpen] = useState(false);

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [editCart, setEditCart] = useState<CartItem[]>([]);
  const [editCustomerDetails, setEditCustomerDetails] = useState<CustomerDetails>({});
  const [editIncludeCustomerDetails, setEditIncludeCustomerDetails] = useState(false);
  const [rawItems, setRawItems] = useState<ItemRaw[]>([]);
  const [rawInv, setRawInv] = useState<InventoryRaw[]>([]);
  const [vatPercentage, setVatPercentage] = useState<number>(0);
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const [companyName, setCompanyName] = useState<string>('Company');
  const [companyPhone, setCompanyPhone] = useState<string>('');
  const [priceAdjustmentLimit, setPriceAdjustmentLimit] = useState<number>(500);
  const [systemTimezone, setSystemTimezone] = useState<string>('Africa/Windhoek');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const [useCustomDate, setUseCustomDate] = useState(false);
  
  // Discount states
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [editDiscountType, setEditDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [editDiscountValue, setEditDiscountValue] = useState<number>(0);

  // Payment method states
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [editPaymentMethod, setEditPaymentMethod] = useState<string>('cash');
  const [showSplitPayment, setShowSplitPayment] = useState<boolean>(false);
  const [isSplitPayment, setIsSplitPayment] = useState<boolean>(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState<boolean>(false);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState<string>('');
  const [cashReceived, setCashReceived] = useState<number>(0);
  const [posVatEnabled, setPosVatEnabled] = useState<boolean>(false);
  const [printDialogOpen, setPrintDialogOpen] = useState<boolean>(false);
  const [completedSaleForPrint, setCompletedSaleForPrint] = useState<Sale | null>(null);
  const [lastReceivedAmount, setLastReceivedAmount] = useState<number | null>(null);
  const [lastChangeAmount, setLastChangeAmount] = useState<number | null>(null);
  const [salePayments, setSalePayments] = useState<any[]>([]);
  const [viewingLayawayPayment, setViewingLayawayPayment] = useState<LayawayHistoryDetails | null>(null);
  const [currentInstallment, setCurrentInstallment] = useState<number | null>(null);
  const [isLoadingLayawayDetails, setIsLoadingLayawayDetails] = useState(false);
  
  // Sales search states
  const [salesSearchQuery, setSalesSearchQuery] = useState('');
  const [salesLoading, setSalesLoading] = useState(false);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [perPage, setPerPage] = useState(10);
  const [branchFilter, setBranchFilter] = useState('all');
  const hasSingleBranch = branches.length === 1;
  const singleBranchName = hasSingleBranch ? branches[0]?.name : undefined;

  const startDateStr = useMemo(() => (dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined), [dateRange.from]);
  const endDateStr = useMemo(() => (dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined), [dateRange.to]);

  // Map of item_id -> item name to show proper names in return dialog
  const itemNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    rawItems.forEach(i => { map[i.id] = i.name; });
    products.forEach(p => { if (!map[p.id]) map[p.id] = p.name; });
    return map;
  }, [rawItems, products]);

  const layawayDialogItems = useMemo(() => {
    if (!selectedSale) {
      return [];
    }

    return selectedSale.record_type === 'layaway_payment'
      ? viewingLayawayPayment?.items || []
      : selectedSale.items || [];
  }, [selectedSale, viewingLayawayPayment?.items]);

  const debouncedSetSearchQuery = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
      setVisibleProductsCount(productsPerLoad);
    }, 300),
    [productsPerLoad]
  );

  const debouncedSetSalesSearchQuery = useCallback(
    debounce((value: string) => {
      setSalesSearchQuery(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 300),
    []
  );

  // Initial data load (excluding sales)
  useEffect(() => {
    async function loadInitialData() {
      try {
        setLoading(true);
        const [settingsRes, itemsRes, invRes, usersRes, branchesRes] = await Promise.all([
          fetchSettings(),
          fetchItems(),
          fetchInventory(),
          fetchUsers(),
          fetchBranches(),
        ]);

        setVatPercentage(settingsRes.data.vat_percentage || 0);
        setCurrencySymbol(settingsRes.data.currency_symbol || '$');
        setSystemTimezone(settingsRes.data.timezone || 'Africa/Windhoek');
        setCompanyName(
          settingsRes.data.company_name ||
          settingsRes.data.business_name ||
          settingsRes.data.store_name ||
          'Company'
        );
        setCompanyPhone(
          settingsRes.data.company_phone ||
          settingsRes.data.phone ||
          settingsRes.data.business_phone ||
          ''
        );
        setSaleDate(toZonedTime(new Date(), settingsRes.data.timezone || 'Africa/Windhoek'));
        const guardValueRaw = settingsRes.data.price_adjustment_limit;
        let guardValue = 500;
        if (typeof guardValueRaw === 'number') {
          guardValue = guardValueRaw;
        } else if (typeof guardValueRaw === 'string' && guardValueRaw.trim() !== '') {
          const parsed = parseFloat(guardValueRaw);
          if (!Number.isNaN(parsed)) {
            guardValue = parsed;
          }
        }
        setPriceAdjustmentLimit(Number.isFinite(guardValue) ? guardValue : 500);
        setRawItems(itemsRes.data);
        setRawInv(invRes.data);
        setUsers(usersRes.data);
        setBranches(branchesRes.data);

        setSelectedUser((prev) => {
          if (prev) return prev;
          if (userId) return userId;
          return usersRes.data[0]?.id || '';
        });

        setSelectedBranch((prev) => prev || '');
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load data');
        console.error('Error loading data:', errorMessage);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, [isAdmin, userBranchId, userId]);

  // Sales data load with pagination and search
  useEffect(() => {
    async function loadSales() {
      try {
        setSalesLoading(true);
        
        const [salesRes, saleItemsRes] = await Promise.all([
          fetchSalesHistory({ 
            page: currentPage, 
            per_page: perPage,
            search: salesSearchQuery,
            start_date: startDateStr,
            end_date: endDateStr,
            branch_id: branchFilter !== 'all' ? branchFilter : undefined,
          }),
          fetchSaleItems(),
        ]);


        const filteredSales = isAdmin
          ? salesRes.data.data
          : salesRes.data.data.filter((sale: Sale) => sale.branch_id === userBranchId);

        const salesWithItems = filteredSales.map((sale: Sale) => {
          const saleItems = saleItemsRes.data.filter((item: SaleItem) => item.sale_id === sale.id);
          const customer = {
            name: sale.customer_name,
            phone: sale.customer_phone,
            address: sale.customer_address,
          };
          const total_amount = typeof sale.total_amount === 'number' ? sale.total_amount : 0;
          const itemCount = Number.isFinite(Number(sale.item_count))
            ? Number(sale.item_count)
            : saleItems.length;
          return {
            ...sale,
            items: sale.record_type === 'sale' ? saleItems : [],
            item_count: itemCount,
            customer: customer.name || customer.phone || customer.address ? customer : undefined,
            total_amount,
          };
        });

        setSales(salesWithItems);
        setPagination({ ...(salesRes.data.pagination || { current_page: 1, per_page: perPage, total: 0, last_page: 1 }), per_page: perPage });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load sales data');
        console.error('Error loading sales:', errorMessage);
      } finally {
        setSalesLoading(false);
      }
    }
    loadSales();
  }, [isAdmin, userBranchId, currentPage, salesSearchQuery, perPage, startDateStr, endDateStr, branchFilter]); // include date filters

  useEffect(() => {
    const enriched: Product[] = rawItems.map(i => {
      const invRecords = rawInv.filter(x => x.item_id === i.id && (!selectedBranch || x.branch_id === selectedBranch));
      // Use available_quantity if it exists (with reserved items), otherwise fall back to quantity
      const totalQuantity = invRecords.reduce((sum, rec) => {
        const availableQty = rec.available_quantity !== undefined ? rec.available_quantity : rec.quantity;
        return sum + parseInt(availableQty, 10);
      }, 0);
      return {
        id: i.id,
        name: i.name,
        sku: i.sku,
        buying_price: parseFloat(i.buying_price) || 0,
        selling_price: parseFloat(i.selling_price),
        quantity: totalQuantity,
        isCustom: i.sku.startsWith('CUS-'),
        vat_enabled: i.vat_enabled ?? 0,
        barcode: i.barcode,
        imeis: i.imeis,
      };
    });

    enriched.sort((a, b) => {
      if (a.quantity === 0 && b.quantity !== 0) return 1;
      if (a.quantity !== 0 && b.quantity === 0) return -1;
      return 0;
    });

    setProducts(enriched);
  }, [rawItems, rawInv, selectedBranch]);

  const filteredProducts = useMemo(() => {
    return products.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.barcode && p.barcode.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [products, searchQuery]);

  const displayedProducts = useMemo(() => {
    return searchQuery ? filteredProducts : filteredProducts.slice(0, visibleProductsCount);
  }, [filteredProducts, visibleProductsCount, searchQuery]);

  // Filter sales based on date range and search query
  const filteredSales = useMemo(() => {
    let list = sales;
    if (dateRange.from || dateRange.to) {
      const fromTime = dateRange.from ? new Date(dateRange.from).setHours(0,0,0,0) : -Infinity;
      const toTime = dateRange.to ? new Date(dateRange.to).setHours(23,59,59,999) : Infinity;
      list = list.filter(sale => {
      const saleTime = new Date(`${sale.created_at}Z`).getTime();
        return saleTime >= fromTime && saleTime <= toTime;
      });
    }
    if (!salesSearchQuery) return list;
    return list.filter(sale => {
      const invoiceMatch = sale.invoice_number.toString().includes(salesSearchQuery);
      const amountMatch = sale.total_amount.toFixed(2).includes(salesSearchQuery);
      const dateMatch = formatDateInCAT(sale.created_at).includes(salesSearchQuery);
      const customerMatch = sale.customer && (
        (sale.customer.name && sale.customer.name.toLowerCase().includes(salesSearchQuery.toLowerCase())) ||
        (sale.customer.phone && sale.customer.phone.includes(salesSearchQuery)) ||
        (sale.customer.address && sale.customer.address.toLowerCase().includes(salesSearchQuery.toLowerCase()))
      );
      return invoiceMatch || amountMatch || dateMatch || customerMatch;
    });
  }, [sales, salesSearchQuery, dateRange]);

  const handleLoadMore = () => {
    setVisibleProductsCount(prev => prev + productsPerLoad);
  };

  const cartSubtotal = useMemo(() => {
    return cart.reduce((sum, x) => sum + x.price * x.quantity, 0);
  }, [cart]);

  const cartVatTotal = useMemo(() => {
    if (!posVatEnabled) return 0;
    return cart.reduce((sum, x) => {
      if (x.vat_enabled) {
        return sum + (x.price * x.quantity * (vatPercentage / 100));
      }
      return sum;
    }, 0);
  }, [cart, vatPercentage, posVatEnabled]);

  const cartSubtotalWithVat = useMemo(() => cartSubtotal + cartVatTotal, [cartSubtotal, cartVatTotal]);
  
  const cartDiscountAmount = useMemo(() => {
    if (discountValue <= 0) return 0;
    if (discountType === 'percentage') {
      return Math.min(cartSubtotalWithVat * (discountValue / 100), cartSubtotalWithVat);
    } else {
      return Math.min(discountValue, cartSubtotalWithVat);
    }
  }, [discountType, discountValue, cartSubtotalWithVat]);
  
  const cartTotal = useMemo(() => Math.max(0, cartSubtotalWithVat - cartDiscountAmount), [cartSubtotalWithVat, cartDiscountAmount]);

  const editCartSubtotal = useMemo(() => {
    return editCart.reduce((sum, x) => sum + x.price * x.quantity, 0);
  }, [editCart]);

  const editCartVatTotal = useMemo(() => {
    return editCart.reduce((sum, x) => {
      if (x.vat_enabled) {
        return sum + (x.price * x.quantity * (vatPercentage / 100));
      }
      return sum;
    }, 0);
  }, [editCart, vatPercentage]);

  const editCartSubtotalWithVat = useMemo(() => editCartSubtotal + editCartVatTotal, [editCartSubtotal, editCartVatTotal]);
  
  const editCartDiscountAmount = useMemo(() => {
    if (editDiscountValue <= 0) return 0;
    if (editDiscountType === 'percentage') {
      return Math.min(editCartSubtotalWithVat * (editDiscountValue / 100), editCartSubtotalWithVat);
    } else {
      return Math.min(editDiscountValue, editCartSubtotalWithVat);
    }
  }, [editDiscountType, editDiscountValue, editCartSubtotalWithVat]);
  
  const editCartTotal = useMemo(() => Math.max(0, editCartSubtotalWithVat - editCartDiscountAmount), [editCartSubtotalWithVat, editCartDiscountAmount]);

  const addToCart = (p: Product) => {
    // Check if item already exists in cart
    const existingCartItem = cart.find(x => x.id === p.id);
    const cartQuantity = existingCartItem ? existingCartItem.quantity : 0;
    
    // Check if adding one more exceeds available stock
    if (!p.isCustom && cartQuantity >= p.quantity) {
      toast.error(`Cannot add more items. Only ${p.quantity} available in stock.`);
      return;
    }
    
    setCart(c => {
      const newCart = c.find(x => x.id === p.id)
        ? c.map(x =>
            x.id === p.id
              ? { ...x, quantity: x.quantity + 1 }
              : x
          )
        : [...c, {
            id: p.id,
            name: p.name,
            price: p.selling_price,
            originalPrice: p.selling_price,
            buying_price: p.buying_price,
            quantity: p.imeis ? 1 : 1,
            isCustom: p.isCustom,
            vat_enabled: p.vat_enabled,
            barcode: p.barcode,
            imeis: p.imeis,
          }];
      return newCart;
    });
  };

  const removeFromCart = (id: string) => {
    setCart(c => {
      const newCart = c
        .map(x => x.id === id ? { ...x, quantity: x.quantity - 1 } : x)
        .filter(x => x.quantity > 0);
      return newCart;
    });
  };

  const handlePriceChange = (id: string, price: number) => {
    setCart(c => {
      const newCart = c.map(x => x.id === id ? { ...x, price } : x);
      return newCart;
    });
  };

  const handleAddCustomItem = async (item: { name: string; price: number }) => {
    const namePattern = /^[A-Za-z0-9\s]+$/;
    if (!namePattern.test(item.name)) {
      toast.error('Invalid item name: only letters, numbers, and spaces are allowed');
      return;
    }

    if (typeof item.price !== 'number' || item.price <= 0 || isNaN(item.price)) {
      toast.error('Invalid price: must be a positive number');
      return;
    }

    if (isAdmin && !selectedBranch) {
      toast.error('Please select a branch before adding a custom item');
      return;
    }

    try {
      const payload = {
        name: item.name,
        buying_price: item.price,
        selling_price: item.price,
        quantity: 1,
        vat_enabled: 0,
      };
      const newItemResponse = await createItem(payload);
      const realItemId = newItemResponse.data.id;

      const invPayload = {
        item_id: realItemId,
        branch_id: selectedBranch || userBranchId,
        quantity: 1,
        invoice_action: 'none',
      };
      await createInventory(invPayload);

      const invRes = await fetchInventory();
      setRawInv(invRes.data);
      const itemsRes = await fetchItems();
      setRawItems(itemsRes.data);

      const newProduct = {
        id: realItemId,
        name: item.name,
        sku: `CUS-${realItemId}`,
        buying_price: item.price,
        selling_price: item.price,
        quantity: 1,
        isCustom: true,
        vat_enabled: 0,
        barcode: null,
        imeis: null,
      };
      setProducts(prev => [...prev, newProduct]);

      setCart(c => [
        ...c,
        {
          id: realItemId,
          name: item.name,
          price: item.price,
          originalPrice: item.price,
          buying_price: item.price,
          quantity: 1,
          isCustom: true,
          vat_enabled: 0,
          barcode: null,
          imeis: null,
        },
      ]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const axiosError = err as { response?: { data?: { error?: string } } };
      toast.error('Failed to add custom item: ' + (axiosError.response?.data?.error || errorMessage));
    }
  };

  const editAddToCart = (p: Product) => {
    setEditCart(c => {
      const newCart = c.find(x => x.id === p.id)
        ? c.map(x =>
            x.id === p.id
              ? { ...x, quantity: x.quantity + 1 }
              : x
          )
        : [...c, {
            id: p.id,
            name: p.name,
            price: p.selling_price,
            originalPrice: p.selling_price,
            buying_price: p.buying_price,
            quantity: p.imeis ? 1 : 1,
            isCustom: p.isCustom,
            vat_enabled: p.vat_enabled,
            barcode: p.barcode,
            imeis: p.imeis,
          }];
      return newCart;
    });
  };

  const editRemoveFromCart = (id: string) => {
    setEditCart(c => {
      const newCart = c
        .map(x => x.id === id ? { ...x, quantity: x.quantity - 1 } : x)
        .filter(x => x.quantity > 0);
      return newCart;
    });
  };

  const editHandlePriceChange = (id: string, price: number) => {
    setEditCart(c => {
      const newCart = c.map(x => x.id === id ? { ...x, price } : x);
      return newCart;
    });
  };

  const editHandleAddCustomItem = async (item: { name: string; price: number }) => {
    const namePattern = /^[A-Za-z0-9\s]+$/;
    if (!namePattern.test(item.name)) {
      toast.error('Invalid item name: only letters, numbers, and spaces are allowed');
      return;
    }

    if (typeof item.price !== 'number' || item.price <= 0 || isNaN(item.price)) {
      toast.error('Invalid price: must be a positive number');
      return;
    }

    if (isAdmin && !selectedBranch) {
      toast.error('Please select a branch before adding a custom item');
      return;
    }

    try {
      const payload = {
        name: item.name,
        buying_price: item.price,
        selling_price: item.price,
        quantity: 1,
        vat_enabled: 0,
      };
      const newItemResponse = await createItem(payload);
      const realItemId = newItemResponse.data.id;

      const invPayload = {
        item_id: realItemId,
        branch_id: selectedBranch || userBranchId,
        quantity: 1,
        invoice_action: 'none',
      };
      await createInventory(invPayload);

      const invRes = await fetchInventory();
      setRawInv(invRes.data);
      const itemsRes = await fetchItems();
      setRawItems(itemsRes.data);

      const newProduct = {
        id: realItemId,
        name: item.name,
        sku: `CUS-${realItemId}`,
        buying_price: item.price,
        selling_price: item.price,
        quantity: 1,
        isCustom: true,
        vat_enabled: 0,
        barcode: null,
        imeis: null,
      };
      setProducts(prev => [...prev, newProduct]);

      setEditCart(c => [
        ...c,
        {
          id: realItemId,
          name: item.name,
          price: item.price,
          originalPrice: item.price,
          buying_price: item.price,
          quantity: 1,
          isCustom: true,
          vat_enabled: 0,
          barcode: null,
          imeis: null,
        },
      ]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const axiosError = err as { response?: { data?: { error?: string } } };
      toast.error('Failed to add custom item: ' + (axiosError.response?.data?.error || errorMessage));
    }
  };

  const formatDateInCAT = (date: string | number | Date): string => {
    const parsed = typeof date === 'string'
      ? new Date(`${date}Z`) // treat backend timestamp as UTC
      : new Date(date);
    return formatInTimeZone(parsed, systemTimezone, "yyyy-MM-dd HH:mm");
  };

  const generatePDFInvoice = async (sale: Sale, autoDownload: boolean = false) => {
    try {
      const settingsRes = await fetchSettings();
      const settings = settingsRes.data;

      const doc = new jsPDF();
      let yOffset = 15;

      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('SALES INVOICE', 105, yOffset, { align: 'center' });
      yOffset += 15;

      const invoiceDate = formatDateInCAT(sale.created_at);
      const invoiceLabel = sale.record_type === 'layaway_payment'
        ? `LP-${sale.layaway_number || sale.invoice_number || 'N/A'}`
        : `INV-${sale.invoice_number || 'N/A'}`;
      const branchName = sale.branch_name || branches.find(b => b.id === sale.branch_id)?.name || '';
      const cashierName = users.find(u => u.id === sale.user_id)?.name || user?.name || '';
      const paymentLabel = sale.is_split_payment || sale.payment_method === 'split'
        ? 'Split Payment'
        : (sale.payment_method || 'N/A');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Invoice #: ${invoiceLabel}`, 14, yOffset);
      doc.text(`Date: ${invoiceDate}`, 140, yOffset);
      yOffset += 6;
      doc.text(`Company: ${settings.company_name || companyName || 'Company'}`, 14, yOffset);
      doc.text(`Branch: ${branchName}`, 140, yOffset);
      yOffset += 6;
      doc.text(`Phone: ${settings.phone || companyPhone || '-'}`, 14, yOffset);
      doc.text(`Cashier: ${cashierName}`, 140, yOffset);
      yOffset += 6;
      doc.text(`Payment: ${paymentLabel}`, 14, yOffset);
      yOffset += 6;

      if (sale.customer?.name) {
        doc.text(`Customer: ${sale.customer.name}`, 14, yOffset);
        yOffset += 6;
      }
      if (sale.customer?.phone) {
        doc.text(`Customer Phone: ${sale.customer.phone}`, 14, yOffset);
        yOffset += 6;
      }
      if (sale.customer?.address) {
        doc.text(`Customer Address: ${sale.customer.address}`, 14, yOffset);
        yOffset += 6;
      }
      yOffset += 5;

      const hasVatInInvoice = (sale.items || []).some((item: any) => Boolean(item.vat_enabled));
      const tableData = (sale.items || []).map((item: any) => {
        const prod = products.find(p => p.id === item.item_id) || rawItems.find(i => i.id === item.item_id);
        const name = prod?.name || 'Custom Item';
        const sku = prod?.sku || item.item_id || '—';
        const qty = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const vatEnabled = Boolean(item.vat_enabled);
        const vatAmt = vatEnabled ? price * qty * (vatPercentage / 100) : 0;
        const lineTot = price * qty + vatAmt;

        return [
          name,
          sku,
          qty.toString(),
          `${currencySymbol}${price.toFixed(2)}`,
          `${currencySymbol}${lineTot.toFixed(2)}`
        ];
      });

      autoTable(doc, {
        startY: yOffset,
        head: [['Item Name', 'SKU', 'Qty', 'Unit Price', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 66, 66] },
        styles: { fontSize: 10, cellPadding: 3, valign: 'middle' },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 78 },
          1: { cellWidth: 42 },
          2: { halign: 'center', cellWidth: 18 },
          3: { halign: 'right', cellWidth: 24 },
          4: { halign: 'right', cellWidth: 24 },
        },
      });

      const finalY = (doc as any).lastAutoTable.finalY + 8;
      let subtotal = 0, totalVat = 0;
      (sale.items || []).forEach((item: any) => {
        const line = item.price * item.quantity;
        subtotal += line;
        if (item.vat_enabled) totalVat += line * (vatPercentage / 100);
      });
      
      const subtotalWithVat = subtotal + totalVat;
      const discountAmount = sale.discount_value && sale.discount_value > 0 ? 
        (sale.discount_type === 'percentage' ? 
          subtotalWithVat * (sale.discount_value / 100) : 
          sale.discount_value) : 0;
      const grandTotal = subtotalWithVat - discountAmount;

      const summaryRows: string[][] = [];
      summaryRows.push(['', '', '', 'Subtotal:', `${currencySymbol}${subtotal.toFixed(2)}`]);
      if (hasVatInInvoice) {
        summaryRows.push(['', '', '', `VAT (${vatPercentage}%):`, `${currencySymbol}${totalVat.toFixed(2)}`]);
      }
      if (discountAmount > 0) {
        summaryRows.push([
          '',
          '',
          '',
          `Discount (${sale.discount_type === 'percentage' ? `${sale.discount_value}%` : `${currencySymbol}${sale.discount_value?.toFixed(2)}`}):`,
          `-${currencySymbol}${discountAmount.toFixed(2)}`
        ]);
      }
      if (lastReceivedAmount !== null) {
        summaryRows.push(['', '', '', 'Received:', `${currencySymbol}${lastReceivedAmount.toFixed(2)}`]);
      }
      if (lastChangeAmount !== null && lastChangeAmount > 0) {
        summaryRows.push(['', '', '', 'Returned:', `${currencySymbol}${lastChangeAmount.toFixed(2)}`]);
      }
      summaryRows.push(['', '', '', 'Grand Total:', `${currencySymbol}${grandTotal.toFixed(2)}`]);

      autoTable(doc, {
        startY: finalY,
        body: summaryRows,
        theme: 'plain',
        styles: { fontSize: 10, halign: 'right' },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 70, fontStyle: 'bold' },
          4: { cellWidth: 35, fontStyle: 'bold' }
        }
      });

      const blobUrl = doc.output('bloburl') as URL;
      const blobUrlString = blobUrl.toString();
      if (autoDownload) {
        const link = document.createElement('a');
        link.href = blobUrlString;
        link.download = `invoice-${sale.invoice_number || 'unknown'}.pdf`;
        link.click();
      } else {
        window.open(blobUrlString, '_blank');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to generate invoice: ' + errorMessage);
    }
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const printThermalInvoice = (sale: Sale) => {
    const items = sale.items || [];
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalVat = items.reduce((sum, item: any) => {
      if (item.vat_enabled) {
        return sum + (item.price * item.quantity * (vatPercentage / 100));
      }
      return sum;
    }, 0);
    const subtotalWithVat = subtotal + totalVat;
    const discountAmount = sale.discount_value && sale.discount_value > 0
      ? (sale.discount_type === 'percentage'
        ? subtotalWithVat * (sale.discount_value / 100)
        : sale.discount_value)
      : 0;
    const grandTotal = Math.max(0, subtotalWithVat - discountAmount);
    const hasVat = items.some((item: any) => Boolean(item.vat_enabled));
    const formatPrintAmount = (amount: number) => `${currencySymbol}${amount.toFixed(2).replace(/\.00$/, '')}`;

    const rowsHtml = items.map((item) => {
      const product = products.find(p => p.id === item.item_id) || rawItems.find(i => i.id === item.item_id);
      const name = escapeHtml(product?.name || 'Item');
      const lineVat = (item as any).vat_enabled ? item.price * item.quantity * (vatPercentage / 100) : 0;
      const lineTotal = (item.price * item.quantity) + lineVat;
      return `
        <tr>
          <td>${name}</td>
          <td class="n">${item.quantity}</td>
          <td class="n">${formatPrintAmount(item.price)}</td>
          <td class="n">${formatPrintAmount(lineTotal)}</td>
        </tr>
      `;
    }).join('');

    const popup = window.open('', '_blank', 'width=420,height=720');
    if (!popup) {
      toast.error('Popup blocked. Please allow popups to print.');
      return;
    }

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Receipt #${sale.invoice_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          html, body { width: 80mm; margin: 0; padding: 0; font-family: Arial, sans-serif; font-size: 13px; color: #000; }
          body { padding: 4mm; box-sizing: border-box; }
          .center { text-align: center; }
          .line { border-top: 1px dashed #000; margin: 6px 0; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { padding: 5px 0; vertical-align: top; }
          th { font-weight: 700; }
          .item-col { width: 54%; }
          .qty-col { width: 12%; }
          .price-col { width: 17%; }
          .total-col { width: 17%; }
          .n { text-align: right; white-space: nowrap; padding-left: 6px; }
          .r { text-align: right; }
          .small { font-size: 12px; color: #222; }
          .total { font-weight: 700; font-size: 16px; }
          .meta div { margin-bottom: 2px; }
        </style>
      </head>
      <body>
        <div class="center">
          <div><strong>${escapeHtml(companyName || 'Company')}</strong></div>
          ${companyPhone ? `<div class="small">${escapeHtml(companyPhone)}</div>` : ''}
        </div>
        <div class="line"></div>
        <div class="meta">
          <div>Invoice: #${sale.invoice_number}</div>
          <div>Date: ${escapeHtml(formatDateInCAT(sale.created_at))}</div>
          <div>Branch: ${escapeHtml(sale.branch_name || branches.find(b => b.id === sale.branch_id)?.name || '')}</div>
          <div>Cashier: ${escapeHtml(users.find(u => u.id === sale.user_id)?.name || user?.name || '')}</div>
          ${sale.customer?.name ? `<div>Customer: ${escapeHtml(sale.customer.name)}</div>` : ''}
        </div>
        <div class="line"></div>
        <table>
          <colgroup>
            <col class="item-col" />
            <col class="qty-col" />
            <col class="price-col" />
            <col class="total-col" />
          </colgroup>
          <thead>
            <tr>
              <th>Item</th>
              <th class="n">Qty</th>
              <th class="n">Price</th>
              <th class="n">Total</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
        <div class="line"></div>
        <table>
          <tr><td>Subtotal</td><td class="r">${formatPrintAmount(subtotal)}</td></tr>
          ${hasVat ? `<tr><td>VAT (${vatPercentage}%)</td><td class="r">${formatPrintAmount(totalVat)}</td></tr>` : ''}
          ${discountAmount > 0 ? `<tr><td>Discount</td><td class="r">-${formatPrintAmount(discountAmount)}</td></tr>` : ''}
          ${lastReceivedAmount !== null ? `<tr><td>Received</td><td class="r">${formatPrintAmount(lastReceivedAmount)}</td></tr>` : ''}
          ${lastChangeAmount !== null && lastChangeAmount > 0 ? `<tr><td>Returned</td><td class="r">${formatPrintAmount(lastChangeAmount)}</td></tr>` : ''}
          <tr class="total"><td>Total</td><td class="r">${formatPrintAmount(grandTotal)}</td></tr>
        </table>
        <script>
          window.onload = function () { window.print(); };
        </script>
      </body>
      </html>
    `;

    popup.document.open();
    popup.document.write(html);
    popup.document.close();
  };

  const handleCompleteSale = async (splitPayments?: any[], paymentMethodOverride?: string) => {
    if (!cart.length) {
      toast.error('Cart is empty');
      return;
    }
    if (!selectedUser || !selectedBranch) {
      toast.error('Please select a user and branch');
      return;
    }
    const resolvedPaymentMethod = splitPayments ? 'split' : (paymentMethodOverride ?? paymentMethod);
    setLoading(true);
    try {
      const saleData = {
        user_id: selectedUser,
        branch_id: selectedBranch,
        total_amount: cartTotal,
        subtotal: cartSubtotalWithVat,
        discount_type: discountValue > 0 ? discountType : null,
        discount_value: discountValue > 0 ? discountValue : 0,
        created_at: useCustomDate ? formatInTimeZone(saleDate, systemTimezone, "yyyy-MM-dd HH:mm:ss") : new Date().toISOString(),
        items: cart.map(item => ({
          item_id: item.id,
          quantity: item.quantity,
          price: item.price,
          vat_enabled: posVatEnabled ? item.vat_enabled : 0,
        })),
        customer_name: includeCustomerDetails ? (customerDetails.name || null) : null,
        customer_phone: includeCustomerDetails ? (customerDetails.phone || null) : null,
        customer_address: includeCustomerDetails ? (customerDetails.address || null) : null,
        payment_method: resolvedPaymentMethod,
        payments: splitPayments || undefined,
      };
      const saleRes = await createSale(saleData);
      const newSale: Sale = {
        ...saleRes.data,
        customer: {
          name: saleData.customer_name,
          phone: saleData.customer_phone,
          address: saleData.customer_address,
        },
        total_amount: saleData.total_amount,
        items: saleData.items.map(item => ({
          id: `${saleRes.data.id}-${item.item_id}`,
          sale_id: saleRes.data.id,
          item_id: item.item_id,
          quantity: item.quantity,
          price: item.price,
        })),
        invoice_number: saleRes.data.invoice_number || 0,
        created_at: saleRes.data.created_at || saleData.created_at,
      };

      toast.success('Sale completed');
      setCompletedSaleForPrint(newSale);
      setPrintDialogOpen(true);

      setCart([]);
      setIncludeCustomerDetails(false);
      setCustomerDetails({});
      setDiscountType('percentage');
      setDiscountValue(0);
      setPaymentMethod('cash');
      setCheckoutPaymentMethod('');
      setCashReceived(0);
      setCurrentPage(1);
      await refreshSales();

      const invRes = await fetchInventory();
      setRawInv(invRes.data);
      const itemsRes = await fetchItems();
      setRawItems(itemsRes.data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const axiosError = err as { response?: { data?: { error?: string } } };
      toast.error('Sale failed: ' + (axiosError.response?.data?.error || errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleSplitPaymentConfirm = async (payments: any[]) => {
    setShowSplitPayment(false);
    const totalPaid = payments.reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
    setLastReceivedAmount(totalPaid);
    setLastChangeAmount(Math.max(0, totalPaid - cartTotal));
    await handleCompleteSale(payments);
  };

  const handleEditSale = (sale: Sale) => {
    setSelectedSale(sale);
    setSelectedUser(sale.user_id);
    setSelectedBranch(sale.branch_id);
    setEditCart((sale.items || []).map(item => {
      const product = products.find(p => p.id === item.item_id);
      return {
        id: item.item_id,
        name: product?.name || 'Custom Item',
        price: item.price,
        originalPrice: product?.selling_price || item.price,
        buying_price: product?.buying_price || 0,
        quantity: item.quantity,
        isCustom: product?.isCustom,
        vat_enabled: product?.vat_enabled ?? 0,
        barcode: product?.barcode,
        imeis: product?.imeis,
      };
    }));
    setEditIncludeCustomerDetails(!!sale.customer);
    setEditCustomerDetails(sale.customer || {});
    setEditDiscountType(sale.discount_type || 'percentage');
    setEditDiscountValue(sale.discount_value || 0);
    setEditPaymentMethod(sale.payment_method || 'cash');
    setEditSaleDialogOpen(true);
    const parsed = new Date(`${sale.created_at}Z`);
    setSaleDate(parsed);
    setUseCustomDate(true);
  };

  const handleUpdateSale = async () => {
    if (!editCart.length) {
      toast.error('Cart is empty');
      return;
    }
    if (!selectedSale) return;
    setLoading(true);
    try {
      const saleData = {
        user_id: isAdmin ? selectedSale.user_id : userId,
        branch_id: isAdmin ? selectedSale.branch_id : userBranchId,
        total_amount: editCartTotal,
        subtotal: editCartSubtotalWithVat,
        discount_type: editDiscountValue > 0 ? editDiscountType : null,
        discount_value: editDiscountValue > 0 ? editDiscountValue : 0,
        payment_method: editPaymentMethod,
        created_at: useCustomDate ? formatInTimeZone(saleDate, systemTimezone, "yyyy-MM-dd HH:mm:ss") : new Date().toISOString(),
        items: editCart.map(item => ({
          item_id: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
        customer_name: editIncludeCustomerDetails ? (editCustomerDetails.name || null) : null,
        customer_phone: editIncludeCustomerDetails ? (editCustomerDetails.phone || null) : null,
        customer_address: editIncludeCustomerDetails ? (editCustomerDetails.address || null) : null,
      };
      const saleRes = await updateSale(selectedSale.id, saleData);
      const updatedSale: Sale = {
        ...saleRes.data,
        customer: {
          name: saleData.customer_name,
          phone: saleData.customer_phone,
          address: saleData.customer_address,
        },
        total_amount: saleData.total_amount,
        items: saleData.items.map(item => ({
          id: `${saleRes.data.id}-${item.item_id}`,
          sale_id: saleRes.data.id,
          item_id: item.item_id,
          quantity: item.quantity,
          price: item.price,
        })),
        invoice_number: saleRes.data.invoice_number || selectedSale.invoice_number,
        created_at: saleRes.data.created_at || saleData.created_at,
      };

      const shouldAutoDownload = !editIncludeCustomerDetails || !(editCustomerDetails.name || editCustomerDetails.phone || editCustomerDetails.address);
      await generatePDFInvoice(updatedSale, shouldAutoDownload);

      toast.success('Sale updated');
      setEditSaleDialogOpen(false);
      setEditCart([]);
      setEditIncludeCustomerDetails(false);
      setEditCustomerDetails({});
      setEditDiscountType('percentage');
      setEditDiscountValue(0);

      setSales(prevSales =>
        prevSales.map(sale =>
          sale.id === selectedSale.id ? updatedSale : sale
        )
      );
      await refreshSales();

      const invRes = await fetchInventory();
      setRawInv(invRes.data);
      const itemsRes = await fetchItems();
      setRawItems(itemsRes.data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const axiosError = err as { response?: { data?: { error?: string } } };
      toast.error('Failed to update sale: ' + (axiosError.response?.data?.error || errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleVoidSale = async (saleId: string) => {
    try {
      await deleteSale(saleId);
      toast.success('Sale voided');
      setCurrentPage(1);
      await refreshSales();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to void sale: ' + errorMessage);
    }
  };

  const handleViewSale = async (sale: Sale) => {
    console.log('Viewing sale:', sale);
    console.log('Sale branch_id:', sale.branch_id);
    console.log('Sale branch_name:', sale.branch_name);
    setViewingLayawayPayment(null);
    setCurrentInstallment(null);
    setIsLoadingLayawayDetails(false);
    setSelectedSale(sale);
    setViewSaleDialogOpen(true);
    
    if (sale.record_type === 'layaway_payment' && sale.layaway_id) {
      try {
        setIsLoadingLayawayDetails(true);
        const layawayRes = await fetchLayaway(sale.layaway_id);
        const layaway = layawayRes.data || {};
        const layawayPayments = Array.isArray(layaway.payments) ? layaway.payments : [];
        setViewingLayawayPayment({
          layaway_id: layaway.id || sale.layaway_id,
          layaway_number: layaway.layaway_number || sale.layaway_number || null,
          items: Array.isArray(layaway.items) ? layaway.items : [],
          payments: layawayPayments,
          customer_name: layaway.customer_name || sale.customer_name || null,
          customer_phone: layaway.customer_phone || sale.customer_phone || null,
          customer_address: layaway.customer_address || sale.customer_address || null,
          status: layaway.status,
          product_handling: layaway.product_handling,
        });
        const sortedPayments = [...layawayPayments].sort((a, b) => new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime());
        const index = sortedPayments.findIndex((payment) => payment.id === sale.id);
        setCurrentInstallment(index >= 0 ? index + 1 : null);
      } catch (error) {
        console.error('Failed to load layaway details:', error);
        toast.error('Failed to load layaway items for this payment.');
        setViewingLayawayPayment(null);
        setCurrentInstallment(null);
      } finally {
        setIsLoadingLayawayDetails(false);
      }
      setSalePayments([]);
      return;
    }

    if (sale.is_split_payment) {
      try {
        const paymentsRes = await fetchSalePayments(sale.id);
        setSalePayments(paymentsRes.data.payments || []);
      } catch (error) {
        console.error('Failed to fetch payment details:', error);
        setSalePayments([]);
      }
      return;
    }
    
    setSalePayments([]);
  };

  const handleReturnSale = (sale: Sale) => {
    setSelectedSale(sale);
    setReturnDialogOpen(true);
  };

  const handleReturnSubmit = async (returnData: any) => {
    try {
      await createReturn(returnData);
      toast.success('Return processed successfully');
      await refreshSales();
      setReturnDialogOpen(false);
      setSelectedSale(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Return failed';
      toast.error('Failed to process return: ' + errorMessage);
      throw error;
    }
  };

  const refreshSales = useCallback(async () => {
    try {
      const salesRes = await fetchSalesHistory({ 
        page: currentPage, 
        per_page: perPage, 
        start_date: startDateStr, 
        end_date: endDateStr,
        branch_id: branchFilter !== 'all' ? branchFilter : undefined,
      });
      const saleItemsRes = await fetchSaleItems();
      const filteredSales = isAdmin
        ? salesRes.data.data
        : salesRes.data.data.filter((sale: Sale) => sale.branch_id === userBranchId);

      const salesWithItems = filteredSales.map((sale: Sale) => {
        const saleItems = saleItemsRes.data.filter((item: SaleItem) => item.sale_id === sale.id);
        const customer = {
          name: sale.customer_name,
          phone: sale.customer_phone,
          address: sale.customer_address,
        };
        const total_amount = typeof sale.total_amount === 'number' ? sale.total_amount : 0;
        const itemCount = Number.isFinite(Number(sale.item_count))
          ? Number(sale.item_count)
          : saleItems.length;
        return {
          ...sale,
          items: sale.record_type === 'sale' ? saleItems : [],
          item_count: itemCount,
          customer: customer.name || customer.phone || customer.address ? customer : undefined,
          total_amount,
        };
      });

      setSales(salesWithItems);
      setPagination({ ...(salesRes.data.pagination || {}), per_page: perPage });
    } catch (err: unknown) {
      toast.error('Failed to refresh sales data');
    }
  }, [isAdmin, userBranchId, currentPage, perPage, startDateStr, endDateStr, branchFilter]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pagination.last_page) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <header>
        <h1 className="text-xl font-semibold">Point of Sale</h1>
      </header>

      <Tabs defaultValue="new-sale">
        <TabsList>
          <TabsTrigger value="new-sale">New Sale</TabsTrigger>
          <TabsTrigger value="sales-history">Sales History</TabsTrigger>
        </TabsList>

        <TabsContent value="new-sale">
          <Card>
            <CardContent className="p-3">
              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                  {isAdmin ? (
                    <>
                      <div>
                        <label className="text-[11px] font-medium block mb-0.5">User</label>
                        <Select value={selectedUser} onValueChange={setSelectedUser}>
                          <SelectTrigger className="w-full h-7 text-sm">
                            <SelectValue placeholder="Select user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(userOption => (
                              <SelectItem key={userOption.id} value={userOption.id}>
                                {userOption.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[11px] font-medium block mb-0.5">Branch</label>
                        {hasSingleBranch ? (
                          <div className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-2 text-sm h-7 flex items-center">
                            {singleBranchName || 'Default branch'}
                          </div>
                        ) : (
                          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                            <SelectTrigger className="w-full h-7 text-sm">
                              <SelectValue placeholder="Select branch..." />
                            </SelectTrigger>
                            <SelectContent>
                              {branches.map(branch => (
                                <SelectItem key={branch.id} value={branch.id}>
                                  {branch.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-[11px] font-medium block mb-0.5">User</label>
                        <div className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-2 text-sm h-7 flex items-center">
                          {user?.name || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <label className="text-[11px] font-medium block mb-0.5">Branch</label>
                        <div className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-3 py-2 text-sm h-7 flex items-center">
                          {branches.find(b => b.id === userBranchId)?.name || singleBranchName || 'Unknown'}
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="text-[11px] font-medium block mb-0.5">Use Custom Date</label>
                    <div className="h-7 flex items-center gap-2">
                      <Checkbox
                        id="use-custom-date"
                        checked={useCustomDate}
                        onCheckedChange={(checked) => setUseCustomDate(!!checked)}
                        className="mr-1"
                      />
                      <label htmlFor="use-custom-date" className="text-sm font-medium">
                        Enable Custom Date
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-medium block mb-0.5">Enable VAT</label>
                    <div className="h-7 flex items-center gap-2">
                      <Checkbox
                        id="pos-vat-enabled"
                        checked={posVatEnabled}
                        onCheckedChange={(checked) => setPosVatEnabled(!!checked)}
                        className="mr-1"
                      />
                      <label htmlFor="pos-vat-enabled" className="text-sm font-medium">
                        Enable VAT
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-medium block mb-0.5">Customer Details</label>
                    <div className="h-7 flex items-center gap-2">
                      <Checkbox
                        id="include-customer-details"
                        checked={includeCustomerDetails}
                        onCheckedChange={(checked) => setIncludeCustomerDetails(!!checked)}
                        className="mr-1"
                      />
                      <label htmlFor="include-customer-details" className="text-sm font-medium">
                        Include Customer Details
                      </label>
                    </div>
                  </div>
                </div>

                {useCustomDate && (
                  <div>
                    <label className="text-[11px] font-medium block mb-0.5">Sale Date ({systemTimezone})</label>
                    <DateTimePicker
                      value={saleDate}
                      onChange={(date) => date && setSaleDate(date)}
                      timezone={systemTimezone}
                      minuteStep={15}
                      use12Hours
                    />
                  </div>
                )}

                {includeCustomerDetails && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[11px] font-medium block mb-0.5">Customer Name</label>
                      <Input
                        value={customerDetails.name || ''}
                        onChange={e => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                        placeholder="Enter customer name"
                        className="w-full h-7 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium block mb-0.5">Phone Number</label>
                      <Input
                        value={customerDetails.phone || ''}
                        onChange={e => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                        placeholder="Enter phone number"
                        className="w-full h-7 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium block mb-0.5">Address</label>
                      <Input
                        value={customerDetails.address || ''}
                        onChange={e => setCustomerDetails({ ...customerDetails, address: e.target.value })}
                        placeholder="Enter address"
                        className="w-full h-7 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-5">
            <Card className="lg:col-span-2">
              <CardHeader className="space-y-3">
                <CardTitle>Products</CardTitle>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <div className="flex items-center gap-2 sm:col-span-3 min-w-0">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search products..."
                      onChange={e => debouncedSetSearchQuery(e.target.value)}
                      className="w-full text-sm"
                    />
                  </div>
                  <div className="sm:col-span-1 min-w-0">
                    <AddCustomItemDialog onAddItem={handleAddCustomItem} />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                  {displayedProducts.map(p => (
                    <ProductCard key={p.id} p={p} addToCart={addToCart} currencySymbol={currencySymbol} />
                  ))}
                </div>
                {!searchQuery && visibleProductsCount < filteredProducts.length && (
                  <div className="text-center">
                    <Button onClick={handleLoadMore} variant="outline" className="text-sm">
                      Load More
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-3">
              <CardHeader className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  <CardTitle>Current Order</CardTitle>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Subtotal: {currencySymbol}{cartSubtotal.toFixed(2)}</div>
                  {posVatEnabled && (
                    <div className="text-sm text-gray-600">VAT ({vatPercentage}%): {currencySymbol}{cartVatTotal.toFixed(2)}</div>
                  )}
                  {cartDiscountAmount > 0 && (
                    <div className="text-sm text-red-600">Discount ({discountType === 'percentage' ? `${discountValue}%` : currencySymbol + discountValue.toFixed(2)}): -{currencySymbol}{cartDiscountAmount.toFixed(2)}</div>
                  )}
                  <div className="text-2xl font-bold">{currencySymbol}{cartTotal.toFixed(2)}</div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-1 border-b" />
                <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/4">Item</TableHead>
                      <TableHead className="w-1/6">Barcode</TableHead>
                      <TableHead className="w-1/6">IMEIs</TableHead>
                      <TableHead className="w-1/6">Qty</TableHead>
                      <TableHead className="w-1/6">Price</TableHead>
                      {posVatEnabled && <TableHead className="w-1/6">VAT</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cart.length === 0 ? (
                      <TableRow className="h-12">
                        <TableCell colSpan={posVatEnabled ? 6 : 5} className="text-center text-muted-foreground py-2">
                          No items in cart
                        </TableCell>
                      </TableRow>
                    ) : (
                      cart.map(item => {
                        const vatAmount = item.vat_enabled ? (item.price * item.quantity * (vatPercentage / 100)) : 0;
                        const isUnderSell = item.price < item.buying_price;
                        return (
                          <TableRow key={item.id} className={`h-14 hover:bg-gray-50 ${isUnderSell ? 'bg-red-50' : ''}`}>
                            <TableCell className="max-w-xs truncate py-2">
                              <div className="flex items-center gap-2">
                                <span className={isUnderSell ? 'text-red-600' : ''}>{item.name}</span>
                                {isUnderSell && (
                                  <Badge variant="destructive" className="text-xs px-1 py-0">
                                    Under-Sell
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-xs truncate py-2">{item.barcode || '—'}</TableCell>
                            <TableCell className="max-w-xs truncate py-2">{item.imeis ? JSON.parse(item.imeis).join(', ') : '—'}</TableCell>
                            <TableCell className="py-2">
                              <div className="flex items-center gap-2">
                                <span className="min-w-6">{item.quantity}</span>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => {
                                    const prod = products.find(p => p.id === item.id);
                                    if (prod) addToCart(prod);
                                  }}
                                  disabled={item.imeis != null}
                                  className="h-7 w-7"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => removeFromCart(item.id)}
                                  className="h-7 w-7"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              <div className="flex items-center gap-2">
                              {currencySymbol}{item.price.toFixed(2)}
                              <EditPricePopover
                                price={item.price}
                                originalPrice={item.originalPrice}
                                priceGuardPercentage={priceAdjustmentLimit}
                                onPriceChange={newPrice => handlePriceChange(item.id, newPrice)}
                              />
                              {item.originalPrice !== item.price && (
                                <span className="text-xs text-muted-foreground line-through">
                                  {currencySymbol}{item.originalPrice.toFixed(2)}
                                </span>
                              )}
                              </div>
                            </TableCell>
                            {posVatEnabled && (
                              <TableCell className="py-2">
                                {item.vat_enabled ? `${currencySymbol}${vatAmount.toFixed(2)}` : '—'}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <div className="p-6 border-t space-y-3">
                  <div className="border rounded-md p-3 space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm font-medium block mb-1">Discount Type</label>
                        <Select value={discountType} onValueChange={(value: 'percentage' | 'fixed') => setDiscountType(value)}>
                          <SelectTrigger className="w-full text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed">Fixed Amount ({currencySymbol})</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-1">
                          Discount Value {discountType === 'percentage' ? '(%)' : `(${currencySymbol})`}
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max={discountType === 'percentage' ? '100' : undefined}
                          step={discountType === 'percentage' ? '0.1' : '0.01'}
                          value={discountValue === 0 ? '' : discountValue}
                          onChange={e => setDiscountValue(Math.max(0, parseFloat(e.target.value) || 0))}
                          placeholder="0.00"
                          className="w-full text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setDiscountType('percentage');
                            setDiscountValue(0);
                          }}
                          className="w-full text-sm"
                        >
                          Clear Discount
                        </Button>
                      </div>
                    </div>
                    {cartDiscountAmount > 0 && (
                      <div className="p-2 bg-green-50 rounded text-sm">
                        <span className="text-green-700">
                          Discount Applied: -{currencySymbol}{cartDiscountAmount.toFixed(2)} 
                          ({discountType === 'percentage' ? `${discountValue}%` : `${currencySymbol}${discountValue.toFixed(2)}`})
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => {
                      setCheckoutPaymentMethod('');
                      setCashReceived(0);
                      setCheckoutDialogOpen(true);
                    }}
                    disabled={!cart.length || loading || !selectedUser || !selectedBranch}
                  >
                    <CircleDollarSign className="mr-2 h-4 w-4" />
                    {loading ? 'Processing...' : 'Complete Sale'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sales-history">
          <Card>
            <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                <CardTitle>Sales History</CardTitle>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`
                        ) : (
                          `${format(dateRange.from, 'PP')} - ...`
                        )
                      ) : (
                        <span>Pick date range</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="end">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const fromDate = startOfToday();
                          const toDate = endOfToday();
                          setDateRange({ from: fromDate, to: toDate });
                          setCurrentPage(1);
                        }}
                      >
                        Today
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const toDate = endOfToday();
                          const fromDate = subDays(startOfToday(), 6);
                          setDateRange({ from: fromDate, to: toDate });
                          setCurrentPage(1);
                        }}
                      >
                        Last 7 days
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const toDate = endOfToday();
                          const fromDate = subDays(startOfToday(), 29);
                          setDateRange({ from: fromDate, to: toDate });
                          setCurrentPage(1);
                        }}
                      >
                        Last 30 days
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const fromDate = startOfMonth(new Date());
                          const toDate = endOfMonth(new Date());
                          setDateRange({ from: fromDate, to: toDate });
                          setCurrentPage(1);
                        }}
                      >
                        This month
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const fromDate = startOfYear(new Date());
                          const toDate = endOfYear(new Date());
                          setDateRange({ from: fromDate, to: toDate });
                          setCurrentPage(1);
                        }}
                      >
                        This year
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDateRange({});
                          setCurrentPage(1);
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range || {});
                        setCurrentPage(1);
                      }}
                      numberOfMonths={2}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {isAdmin && (
                  <Select value={branchFilter} onValueChange={(value) => {
                    setBranchFilter(value);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-center gap-2 w-64">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by invoice #, amount, date, customer..."
                    onChange={e => debouncedSetSalesSearchQuery(e.target.value)}
                    className="w-full text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {salesLoading ? (
                <div className="text-center py-8">
                  <div className="text-muted-foreground">Loading sales...</div>
                </div>
              ) : (
                <>
                  <Table className="min-w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/8 text-left">Invoice #</TableHead>
                        <TableHead className="w-1/8 text-left">Date (CAT)</TableHead>
                        <TableHead className="w-1/8 text-left">Total Amount</TableHead>
                        <TableHead className="w-1/8 text-left">Discount</TableHead>
                        <TableHead className="w-1/8 text-left">Status</TableHead>
                        <TableHead className="w-1/8 text-left">Items</TableHead>
                        <TableHead className="w-1/8 text-left">Customer</TableHead>
                        {isAdmin && <TableHead className="w-1/8 text-left">Branch</TableHead>}
                        <TableHead className="w-1/8 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSales.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-muted-foreground py-8">
                            {salesSearchQuery ? 'No sales found matching your search' : 'No sales found'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSales.map(sale => (
                          <TableRow key={`${sale.record_type || 'sale'}-${sale.id}`} className="hover:bg-gray-50">
                            <TableCell className="max-w-xs truncate font-medium">
                              {sale.record_type === 'layaway_payment'
                                ? `LP #${sale.layaway_number || sale.invoice_number}`
                                : sale.invoice_number || 'N/A'}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{formatDateInCAT(sale.created_at)}</TableCell>
                            <TableCell className="max-w-xs truncate font-medium">{currencySymbol}{(sale.total_amount ?? 0).toFixed(2)}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {(sale.discount_value ?? 0) > 0 ? (
                                <span className="text-green-600">
                                  {sale.discount_type === 'percentage' ? `${sale.discount_value}%` : `${currencySymbol}${sale.discount_value.toFixed(2)}`}
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {sale.record_type === 'layaway_payment' ? (
                                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                                  Layaway Payment
                                </Badge>
                              ) : sale.has_returns ? (
                                <div className="flex flex-col">
                                  {(sale.total_returned_amount ?? 0) >= (sale.total_amount ?? 0) ? (
                                    <Badge variant="secondary" className="bg-red-100 text-red-800 mb-1">
                                      Fully Returned
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="bg-orange-100 text-orange-800 mb-1">
                                      Partial Return
                                    </Badge>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    -{currencySymbol}{(sale.total_returned_amount || 0).toFixed(2)}
                                  </span>
                                </div>
                              ) : (
                                <Badge variant="default" className="bg-green-100 text-green-800">
                                  Complete
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{sale.item_count ?? (sale.items?.length ?? 0)} items</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {sale.customer?.name || sale.customer?.phone || '—'}
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="max-w-xs truncate">
                                {sale.branch_name || 'N/A'}
                              </TableCell>
                            )}
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="icon" onClick={() => handleViewSale(sale)} className="h-8 w-8">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => generatePDFInvoice(sale, false)} className="h-8 w-8">
                                  <Download className="h-4 w-4" />
                                </Button>
                                {sale.record_type !== 'layaway_payment' && (
                                  <>
                                    <Button variant="ghost" size="icon" onClick={() => handleEditSale(sale)} className="h-8 w-8">
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      onClick={() => handleReturnSale(sale)} 
                                      className="h-8 w-8" 
                                      title={sale.has_returns && (sale.total_returned_amount ?? 0) >= (sale.total_amount ?? 0) ? "Sale fully returned" : "Process Return"}
                                      disabled={sale.has_returns && (sale.total_returned_amount ?? 0) >= (sale.total_amount ?? 0)}
                                    >
                                      <RotateCcw className={`h-4 w-4 ${sale.has_returns && (sale.total_returned_amount ?? 0) >= (sale.total_amount ?? 0) ? 'text-gray-400' : 'text-orange-600'}`} />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleVoidSale(sale.id)} className="h-8 w-8">
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination + Per-page */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                    <div className="text-sm text-muted-foreground">
                      {(() => {
                        if (!pagination.total) return 'Showing 0-0 of 0 sales';
                        const start = (pagination.current_page - 1) * pagination.per_page + 1;
                        const end = Math.min(pagination.current_page * pagination.per_page, pagination.total);
                        return `Showing ${start}-${end} of ${pagination.total} sales`;
                      })()}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Per page:</span>
                        <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setCurrentPage(1); }}>
                          <SelectTrigger className="h-8 w-[90px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          disabled={currentPage === 1 || salesLoading}
                          onClick={handlePreviousPage}
                          variant="outline"
                          className="px-4 py-2 text-sm"
                        >
                          Previous
                        </Button>
                        <Button
                          disabled={currentPage === pagination.last_page || salesLoading}
                          onClick={handleNextPage}
                          variant="outline"
                          className="px-4 py-2 text-sm"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Search Results Info */}
                  {salesSearchQuery && (
                    <div className="text-center mt-4 text-sm text-muted-foreground">
                      {filteredSales.length > 0 
                        ? `Found ${filteredSales.length} sales matching "${salesSearchQuery}"`
                        : `No sales found matching "${salesSearchQuery}"`
                      }
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={viewSaleDialogOpen}
        onOpenChange={(open) => {
          setViewSaleDialogOpen(open);
          if (!open) {
            setViewingLayawayPayment(null);
            setCurrentInstallment(null);
            setIsLoadingLayawayDetails(false);
            setSalePayments([]);
          }
        }}
      >
        <DialogContent className="w-[96vw] max-w-[1150px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-3xl font-bold tracking-tight text-slate-900">
                  {selectedSale?.record_type === 'layaway_payment' ? 'Layaway Payment Details' : 'Sale Details'}
                </DialogTitle>
                <DialogDescription className="mt-2 text-base text-slate-600">
                  {selectedSale?.record_type === 'layaway_payment'
                    ? `Layaway #${selectedSale.layaway_number || selectedSale.invoice_number || 'N/A'} • Payment ${currentInstallment || '?'}`
                    : `Invoice #${selectedSale?.invoice_number || 'N/A'}`}
                </DialogDescription>
              </div>
              {selectedSale?.record_type === 'layaway_payment' ? (
                <div className={`px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800`}>
                  {`Layaway Payment (${currentInstallment ? `Installment ${currentInstallment}` : 'Payment'})`}
                </div>
              ) : (!!selectedSale?.has_returns && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  (selectedSale.total_returned_amount ?? 0) >= (selectedSale.total_amount ?? 0) 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {(selectedSale.total_returned_amount ?? 0) >= (selectedSale.total_amount ?? 0) 
                    ? 'Fully Returned' 
                    : 'Partially Returned'
                  }
                </div>
              ))}
            </div>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-6">
              {/* Sale Information Card */}
              <div className="rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 p-6 shadow-sm">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Date & Time:</span>
                      <span className="text-gray-900 font-semibold">{formatDateInCAT(selectedSale.created_at)}</span>
                    </div>
                    {isAdmin && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Branch:</span>
                        <span className="text-gray-900 font-semibold">{selectedSale.branch_name || 'N/A'}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Subtotal:</span>
                      <span className="text-gray-900 font-semibold">{currencySymbol}{(selectedSale.subtotal ?? selectedSale.total_amount ?? 0).toFixed(2)}</span>
                    </div>
                    {(selectedSale.discount_value ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Discount:</span>
                        <span className="text-green-600 font-semibold">
                          {selectedSale.discount_type === 'percentage' ? `${selectedSale.discount_value}%` : `${currencySymbol}${selectedSale.discount_value.toFixed(2)}`}
                          {' '}(-{currencySymbol}{selectedSale.discount_type === 'percentage' 
                            ? ((selectedSale.subtotal ?? selectedSale.total_amount ?? 0) * selectedSale.discount_value / 100).toFixed(2)
                            : selectedSale.discount_value.toFixed(2)})
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Payment Method:</span>
                      <span className="text-gray-900 font-semibold">
                        {selectedSale.is_split_payment ? (
                          <span className="text-purple-600 font-semibold">Split Payment</span>
                        ) : (
                          <span className="capitalize">{selectedSale.payment_method?.replace('_', ' ') || 'Cash'}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Total Amount:</span>
                      <span className="text-lg font-bold text-gray-900">{currencySymbol}{(selectedSale.total_amount ?? 0).toFixed(2)}</span>
                    </div>
                    {!!selectedSale.has_returns && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Returned:</span>
                          <span className="text-orange-600 font-semibold">-{currencySymbol}{(selectedSale.total_returned_amount ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-t border-gray-200">
                          <span className="text-gray-800 font-bold">Net Amount:</span>
                          <span className="text-lg font-bold text-green-600">{currencySymbol}{(selectedSale.net_amount ?? selectedSale.total_amount ?? 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Information Card */}
              {selectedSale.customer && (selectedSale.customer.name || selectedSale.customer.phone || selectedSale.customer.address) ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {selectedSale.customer.name && (
                      <div>
                        <span className="text-gray-600 text-sm font-medium block">Name</span>
                        <span className="text-gray-900 font-semibold">{selectedSale.customer.name}</span>
                      </div>
                    )}
                    {selectedSale.customer.phone && (
                      <div>
                        <span className="text-gray-600 text-sm font-medium block">Phone</span>
                        <span className="text-gray-900 font-semibold">{selectedSale.customer.phone}</span>
                      </div>
                    )}
                    {selectedSale.customer.address && (
                      <div>
                        <span className="text-gray-600 text-sm font-medium block">Address</span>
                        <span className="text-gray-900 font-semibold">{selectedSale.customer.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Payment Details Card */}
              {selectedSale.is_split_payment && salePayments.length > 0 ? (
                <div className="rounded-2xl border border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                    Split Payment Details
                  </h3>
                  <div className="space-y-3">
                    {salePayments.map((payment, index) => (
                        <div key={payment.id} className="flex flex-col gap-3 rounded-xl border border-purple-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 capitalize">
                              {payment.payment_method?.replace('_', ' ')}
                            </div>
                            {payment.reference_number && (
                              <div className="text-sm text-gray-600">
                                Ref: {payment.reference_number}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {currencySymbol}{(payment.amount ?? 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedSale.record_type === 'layaway_payment' && viewingLayawayPayment ? (
                <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Layaway Payment History
                  </h3>
                  <div className="space-y-3">
                    {[...viewingLayawayPayment.payments]
                      .sort((a, b) => new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime())
                      .map((payment, idx) => (
                        <div key={payment.id} className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <CreditCard className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                Installment {idx + 1}
                              </div>
                              <div className="text-sm text-gray-600">
                                {payment.payment_method ? payment.payment_method.replace('_', ' ') : 'cash'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatDateInCAT(payment.payment_date)}
                              </div>
                            </div>
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {currencySymbol}{(payment.amount ?? 0).toFixed(2)}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              ) : null}

              {/* Items Table */}
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    {selectedSale.record_type === 'layaway_payment' ? (
                      <span>Layaway Items ({selectedSale.item_count ?? layawayDialogItems.length})</span>
                    ) : (
                      <span>Sale Items ({(selectedSale.items || []).length})</span>
                    )}
                  </h3>
                  {selectedSale.record_type === 'layaway_payment' && (
                    <p className="text-sm text-gray-600 mt-1">
                      Showing items from Layaway #{selectedSale.layaway_number || selectedSale.invoice_number}. 
                      {` This is installment ${currentInstallment || '?'}${viewingLayawayPayment?.payments?.length ? ` of ${viewingLayawayPayment.payments.length}` : ''} paid on ${formatDateInCAT(selectedSale.created_at)}.`}
                      {isLoadingLayawayDetails ? ' Loading layaway item details...' : ''}
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <Table className="min-w-[860px]">
                    <TableHeader>
                      <TableRow className="bg-gray-50 hover:bg-gray-50">
                        <TableHead className="font-semibold text-gray-900">Item</TableHead>
                        <TableHead className="font-semibold text-gray-900">Barcode</TableHead>
                        <TableHead className="font-semibold text-gray-900">IMEIs</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-center">Qty</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-right">Unit Price</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-right">VAT</TableHead>
                        <TableHead className="font-semibold text-gray-900 text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingLayawayDetails ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            Loading layaway item details...
                          </TableCell>
                        </TableRow>
                      ) : layawayDialogItems.length > 0 ? (
                        layawayDialogItems.map((item: any, index) => {
                          const product = products.find(p => p.id === item.item_id) || rawItems.find(i => i.id === item.item_id);
                          const vatEnabled = product?.vat_enabled ?? Number(item.vat_enabled || 0);
                          const vatAmount = vatEnabled ? (item.quantity * item.price * (vatPercentage / 100)) : 0;
                          const totalWithVat = (item.quantity * item.price) + vatAmount;
                          const itemName = product?.name || item.name || 'Custom Item';
                          const itemBarcode = product?.barcode || item.barcode || item.sku || '—';
                          const itemImeis = item.imeis || '';
                          return (
                            <TableRow key={item.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                              <TableCell className="font-medium text-gray-900">
                                <div className="max-w-xs">
                                  <div className="font-semibold truncate">{itemName}</div>
                                  {product?.description ? (
                                    <div className="text-sm text-gray-500 truncate">{String(product.description)}</div>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell className="text-gray-600">
                                <div className="max-w-xs truncate font-mono text-sm">
                                  {itemBarcode}
                                </div>
                              </TableCell>
                              <TableCell className="text-gray-600">
                                <div className="max-w-xs truncate text-sm">
                                  {itemImeis ? JSON.parse(itemImeis).join(', ') : '—'}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-semibold">
                                  {item.quantity}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-gray-900">
                                {currencySymbol}{(item.price ?? 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right text-gray-600">
                                {vatAmount > 0 ? `${currencySymbol}${vatAmount.toFixed(2)}` : '—'}
                              </TableCell>
                              <TableCell className="text-right font-bold text-gray-900">
                                {currencySymbol}{totalWithVat.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            {selectedSale.record_type === 'layaway_payment'
                              ? `No layaway items found for payment #${selectedSale.layaway_number || selectedSale.id}.`
                              : 'No items found for this sale.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editSaleDialogOpen} onOpenChange={setEditSaleDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[1100px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Edit Sale</DialogTitle>
            <DialogDescription className="text-sm">
              Modify the sale details and items.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="text-lg font-semibold mb-2">Products</h3>
              <div className="mb-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  defaultValue={searchQuery}
                  onChange={e => debouncedSetSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredProducts.map(p => (
                  <Card
                    key={p.id}
                    className={`p-4 flex justify-between items-center rounded-lg shadow-md transition-transform transform hover:scale-105 hover:shadow-lg ${
                      p.quantity <= 0 && !p.isCustom ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    style={{
                      background: p.isCustom
                        ? 'linear-gradient(135deg, #e0e7ff 0%, #c3dafe 100%)'
                        : 'linear-gradient(135deg, #e6f3ff 0%, #d1e9ff 100%)',
                      border: 'none',
                    }}
                  >
                    <div className="flex-1">
                      <span className="font-semibold text-gray-800 block truncate">{p.name}</span>
                      <span className="text-muted-foreground block text-sm text-gray-600 truncate">SKU: {p.sku}</span>
                      <span className="text-muted-foreground block text-sm text-gray-600 truncate">Price: {currencySymbol}{p.selling_price.toFixed(2)}</span>
                      <span className="text-muted-foreground block text-sm text-gray-600 truncate">Stock: {p.quantity}</span>
                      {p.barcode && (
                        <span className="text-muted-foreground block text-sm text-gray-600 truncate">Barcode: {p.barcode}</span>
                      )}
                      {p.imeis && (
                        <span className="text-muted-foreground block text-sm text-gray-600 truncate">IMEIs: {JSON.parse(p.imeis).join(', ')}</span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-gray-800 hover:bg-gray-100 rounded-full shadow ml-2"
                      onClick={() => editAddToCart(p)}
                      disabled={(p.quantity <= 0 && !p.isCustom) || p.imeis != null}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </Card>
                ))}
              </div>
              <AddCustomItemDialog onAddItem={editHandleAddCustomItem} />
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 flex justify-between items-center">
                <span>Current Order</span>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Subtotal: {currencySymbol}{editCartSubtotal.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">VAT ({vatPercentage}%): {currencySymbol}{editCartVatTotal.toFixed(2)}</div>
                  {editCartDiscountAmount > 0 && (
                    <div className="text-sm text-red-600">Discount ({editDiscountType === 'percentage' ? `${editDiscountValue}%` : currencySymbol + editDiscountValue.toFixed(2)}): -{currencySymbol}{editCartDiscountAmount.toFixed(2)}</div>
                  )}
                  <div className="text-lg font-bold">{currencySymbol}{editCartTotal.toFixed(2)}</div>
                </div>
              </h3>
              <div className="space-y-4">
                {isAdmin && selectedSale && (
                  <>
                    <div>
                      <label className="text-sm font-medium block mb-1">User</label>
                      <Select value={selectedUser} onValueChange={setSelectedUser} defaultValue={selectedSale.user_id}>
                        <SelectTrigger className="w-full text-sm">
                          <SelectValue placeholder="Select user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(user => (
                            <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Branch</label>
                      <Select value={selectedBranch} onValueChange={setSelectedBranch} defaultValue={selectedSale.branch_id}>
                        <SelectTrigger className="w-full text-sm">
                          <SelectValue placeholder="Select branch..." />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.map(branch => (
                            <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {!isAdmin && (
                  <div>
                    <p className="text-sm font-medium">Branch: {branches.find(b => b.id === userBranchId)?.name || 'Unknown'}</p>
                    <p className="text-sm font-medium">User: {user?.name || 'Unknown'}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1">Use Custom Date</label>
                  <Checkbox
                    id="use-custom-date"
                    checked={useCustomDate}
                    onCheckedChange={(checked) => setUseCustomDate(!!checked)}
                    className="mr-2"
                  />
                  <label htmlFor="use-custom-date" className="text-sm font-medium">
                    Enable Custom Date
                  </label>
                </div>
                {useCustomDate && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Sale Date ({systemTimezone})</label>
                    <DateTimePicker
                      value={saleDate}
                      onChange={(date) => date && setSaleDate(date)}
                      timezone={systemTimezone}
                      minuteStep={15}
                      use12Hours
                    />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1">Payment Method</label>
                  <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="debit_card">Debit Card</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-include-customer-details"
                    checked={editIncludeCustomerDetails}
                    onCheckedChange={(checked) => setEditIncludeCustomerDetails(!!checked)}
                    className="mr-2"
                  />
                  <label htmlFor="edit-include-customer-details" className="text-sm font-medium">
                    Include Customer Details
                  </label>
                </div>
                {editIncludeCustomerDetails && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-1">Customer Name</label>
                      <Input
                        value={editCustomerDetails.name || ''}
                        onChange={e => setEditCustomerDetails({ ...editCustomerDetails, name: e.target.value })}
                        placeholder="Enter customer name"
                        className="w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Phone Number</label>
                      <Input
                        value={editCustomerDetails.phone || ''}
                        onChange={e => setEditCustomerDetails({ ...editCustomerDetails, phone: e.target.value })}
                        placeholder="Enter phone number"
                        className="w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Address</label>
                      <Input
                        value={editCustomerDetails.address || ''}
                        onChange={e => setEditCustomerDetails({ ...editCustomerDetails, address: e.target.value })}
                        placeholder="Enter address"
                        className="w-full text-sm"
                      />
                    </div>
                  </div>
                )}
                
                {/* Edit Discount Section */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-3">Discount</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium block mb-1">Discount Type</label>
                      <Select value={editDiscountType} onValueChange={(value: 'percentage' | 'fixed') => setEditDiscountType(value)}>
                        <SelectTrigger className="w-full text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount ({currencySymbol})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">
                        Discount Value {editDiscountType === 'percentage' ? '(%)' : `(${currencySymbol})`}
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max={editDiscountType === 'percentage' ? '100' : undefined}
                        step={editDiscountType === 'percentage' ? '0.1' : '0.01'}
                        value={editDiscountValue === 0 ? '' : editDiscountValue}
                        onChange={e => setEditDiscountValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        placeholder={editDiscountType === 'percentage' ? '0.00' : '0.00'}
                        className="w-full text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditDiscountType('percentage');
                          setEditDiscountValue(0);
                        }}
                        className="w-full text-sm"
                      >
                        Clear Discount
                      </Button>
                    </div>
                  </div>
                  {editCartDiscountAmount > 0 && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <span className="text-green-700">
                        Discount Applied: -{currencySymbol}{editCartDiscountAmount.toFixed(2)} 
                        ({editDiscountType === 'percentage' ? `${editDiscountValue}%` : `${currencySymbol}${editDiscountValue.toFixed(2)}`})
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/4">Item</TableHead>
                    <TableHead className="w-1/6">Barcode</TableHead>
                    <TableHead className="w-1/6">IMEIs</TableHead>
                    <TableHead className="w-1/12">Qty</TableHead>
                    <TableHead className="w-1/6">Price</TableHead>
                    <TableHead className="w-1/6">VAT</TableHead>
                    <TableHead className="w-1/6 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editCart.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No items in cart
                      </TableCell>
                    </TableRow>
                  ) : (
                    editCart.map(item => {
                      const vatAmount = item.vat_enabled ? (item.price * item.quantity * (vatPercentage / 100)) : 0;
                      const isUnderSell = item.price < item.buying_price;
                      return (
                        <TableRow key={item.id} className={`hover:bg-gray-50 ${isUnderSell ? 'bg-red-50' : ''}`}>
                          <TableCell className="max-w-xs truncate">
                            <div className="flex items-center gap-2">
                              <span className={isUnderSell ? 'text-red-600' : ''}>{item.name}</span>
                              {isUnderSell && (
                                <Badge variant="destructive" className="text-xs px-1 py-0">
                                  Under-Sell
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{item.barcode || '—'}</TableCell>
                          <TableCell className="max-w-xs truncate">{item.imeis ? JSON.parse(item.imeis).join(', ') : '—'}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell className="flex items-center gap-2">
                            {currencySymbol}{item.price.toFixed(2)}
                            <EditPricePopover
                              price={item.price}
                              originalPrice={item.originalPrice}
                              priceGuardPercentage={priceAdjustmentLimit}
                              onPriceChange={newPrice => editHandlePriceChange(item.id, newPrice)}
                            />
                            {item.originalPrice !== item.price && (
                              <span className="text-xs text-muted-foreground line-through">
                                {currencySymbol}{item.originalPrice.toFixed(2)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.vat_enabled ? `${currencySymbol}${vatAmount.toFixed(2)}` : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  const prod = products.find(p => p.id === item.id);
                                  if (prod) editAddToCart(prod);
                                }}
                                disabled={item.imeis != null}
                                className="h-8 w-8"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => editRemoveFromCart(item.id)}
                                className="h-8 w-8"
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              </div>
              <div className="p-6 border-t">
                <Button
                  className="w-full"
                  onClick={handleUpdateSale}
                  disabled={!editCart.length || loading}
                >
                  <CircleDollarSign className="mr-2 h-4 w-4" />
                  {loading ? 'Processing...' : 'Update Sale'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <ReturnDialog
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        sale={selectedSale}
        onSubmit={handleReturnSubmit}
        currencySymbol={currencySymbol}
        itemNameMap={itemNameMap}
      />

      <Dialog
        open={checkoutDialogOpen}
        onOpenChange={(open) => {
          setCheckoutDialogOpen(open);
          if (open) {
            setCheckoutPaymentMethod('');
            setCashReceived(0);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Complete Sale</DialogTitle>
            <DialogDescription>Select payment method to continue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/20 p-3 space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>{currencySymbol}{cartSubtotal.toFixed(2)}</span>
              </div>
              {posVatEnabled && cartVatTotal > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">VAT ({vatPercentage}%)</span>
                  <span>{currencySymbol}{cartVatTotal.toFixed(2)}</span>
                </div>
              )}
              {cartDiscountAmount > 0 && (
                <div className="flex items-center justify-between text-red-600">
                  <span>Discount</span>
                  <span>-{currencySymbol}{cartDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t font-semibold text-base">
                <span>Amount Due</span>
                <span>{currencySymbol}{cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Payment Method</label>
              <Select value={checkoutPaymentMethod || undefined} onValueChange={setCheckoutPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {checkoutPaymentMethod === 'cash' && (
              <div className="space-y-1">
                <label className="text-sm font-medium block mb-1">Cash Received</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashReceived || ''}
                  onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
                {cashReceived >= cartTotal ? (
                  <div className="text-sm text-gray-600">
                    Return to customer: {currencySymbol}{(cashReceived - cartTotal).toFixed(2)}
                  </div>
                ) : (
                  <div className="text-sm text-red-600">
                    Remaining to collect: {currencySymbol}{(cartTotal - cashReceived).toFixed(2)}
                  </div>
                )}
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setCheckoutDialogOpen(false);
                setShowSplitPayment(true);
              }}
              disabled={!cart.length || loading || !selectedUser || !selectedBranch}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Split Payment
            </Button>

            <Button
              className="w-full"
              onClick={async () => {
                if (!checkoutPaymentMethod) {
                  toast.error('Please select a payment method');
                  return;
                }
                if (checkoutPaymentMethod === 'cash' && cashReceived < cartTotal) {
                  toast.error('Cash received must be equal to or more than total');
                  return;
                }
                if (checkoutPaymentMethod === 'cash') {
                  setLastReceivedAmount(cashReceived);
                  setLastChangeAmount(Math.max(0, cashReceived - cartTotal));
                } else {
                  setLastReceivedAmount(null);
                  setLastChangeAmount(null);
                }
                setPaymentMethod(checkoutPaymentMethod);
                setCheckoutDialogOpen(false);
                await handleCompleteSale(undefined, checkoutPaymentMethod);
              }}
              disabled={!cart.length || loading || !selectedUser || !selectedBranch}
            >
              Confirm & Complete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Split Payment Dialog */}
      <SplitPaymentDialog
        isOpen={showSplitPayment}
        onClose={() => setShowSplitPayment(false)}
        totalAmount={cartTotal}
        onConfirm={handleSplitPaymentConfirm}
      />

      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Sale Completed</DialogTitle>
            <DialogDescription>Select invoice print type.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button
              className="w-full"
              onClick={() => {
                if (completedSaleForPrint) {
                  generatePDFInvoice(completedSaleForPrint, false);
                }
              }}
            >
              <FileText className="mr-2 h-4 w-4" />
              Print A4 Invoice
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                if (completedSaleForPrint) {
                  printThermalInvoice(completedSaleForPrint);
                }
              }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print Thermal Invoice
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setPrintDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
